<template>
    <div>
        <vx-card class="p-4 mb-6">
            <div class="flex flex-wrap items-center justify-between mb-4">
                <div class="flex items-center">
                    <h4 style="margin-left: 10px">{{ pageTitle }}</h4>
                </div>
            </div>

            <div class="mb-4 pl-4 mt-4">
                <div class="flex flex-wrap items-end">
                    <div class="mr-4 mb-3 sm:mb-0 min-w-[200px]">
                        <label class="text-sm font-medium text-gray-700 block mb-1">Reference</label>
                        <vs-input
                            v-model="filterReference"
                            placeholder="Enter reference"
                            class="w-full"
                            @keyup.enter="applyFilter"
                        />
                    </div>
                    <div class="mr-4 mb-3 sm:mb-0 min-w-[200px]" ref="statusWrapper">
                        <label class="text-sm font-medium text-gray-700 block mb-1">Status</label>
                        <vs-select
                            ref="statusSelect"
                            v-model="filterStatus"
                            placeholder="All Status"
                            class="w-full"
                        >
                            <vs-select-item value="" text="All Status" />
                            <vs-select-item value="ongoing" text="Ongoing" />
                            <vs-select-item value="done" text="Done" />
                        </vs-select>
                    </div>
                    <div class="flex items-center mt-2 sm:mt-0">
                        <vs-button @click="applyFilter" icon="search" class="mr-2">Filter</vs-button>
                        <vs-button color="danger" @click="clearFilter" icon="refresh">Reset</vs-button>
                    </div>
                </div>
            </div>

            <ag-grid-vue
                :gridOptions="gridOptions"
                class="my-4 ag-theme-material w-100 ag-grid-table"
                :columnDefs="columnDefs"
                :defaultColDef="defaultColDef"
                :rowData="outbounds"
                colResizeDefault="shift"
                :animateRows="false"
                :floatingFilter="false"
                :pagination="true"
                :suppressPaginationPanel="true"
                :paginationPageSize="paginationPageSize"
                rowSelection="single"
                @selection-changed="onSelectionChanged"
                @rowDoubleClicked="processOnDblClick"
                @grid-ready="onGridReady"
            >
            </ag-grid-vue>

            <p class="text-right">
                <b>
                    Showing {{ currentPage * paginationPageSize - (paginationPageSize - 1) }} to {{ outbounds.length - currentPage * paginationPageSize > 0 ? currentPage * paginationPageSize : outbounds.length }} of
                    {{ outbounds.length }} total entries
                </b>
            </p>

            <vs-pagination
                :total="totalPages"
                :max="maxPageNumbers"
                v-model="currentPage"
            />
        </vx-card>

        <vs-prompt
            v-if="!useVendorWarehouseModal"
            @accept="processItem"
            :active.sync="warehouseSelectionModal"
            title="Select Warehouse"
            :is-valid="warehouse.form.warehouse_id ? true : false"
        >
            <div class="con-exemple-prompt">
                <label class="vs-input--label">Warehouse</label>
                <vs-select
                    class="selectExample mb-2"
                    v-model="warehouse.form.warehouse_id"
                    width="100%"
                    autocomplete
                    @input-change="searchDropdown($event, warehouse)"
                    placeholder="Select Warehouse"
                >
                    <vs-select-item
                        v-for="(item, index) in warehouse.data"
                        :key="index"
                        :value="item.id"
                        :text="item.warehouses_code"
                    />
                </vs-select>
            </div>
        </vs-prompt>

        <VendorWarehouseModal
            v-if="useVendorWarehouseModal"
            :active.sync="vendorWarehouseModal"
            :outbound="selectedOutbound"
            @submit="submitVendorWarehouse"
        />
    </div>
</template>

<script>
    import { AgGridVue } from "ag-grid-vue";
    import "@/assets/scss/vuesax/extraComponents/agGridStyleOverride.scss";
    import VendorWarehouseModal from "./VendorWarehouseModal.vue";
    import { ORDER_TYPE } from "@/constants/outbound";

    export default {
        name: "GiVendorIndex",

        components: {
            AgGridVue,
            VendorWarehouseModal,
        },

        props: {
            pageTitle: {
                type: String,
                default: "GI Vendor"
            },
            isCentralWarehouse: {
                type: Boolean,
                default: true
            },
            commitRouteName: {
                type: String,
                default: ""
            },
            orderType: {
                type: [String],
                default: ''
            },
            useVendorWarehouseModal: {
                type: Boolean,
                default: false
            },
            includeCentralWarehouseParam: {
                type: Boolean,
                default: false
            },
            skipWarehouseSelection: {
                type: Boolean,
                default: false
            }
        },

        data() {
            return {
                maxPageNumbers: 7,
                gridOptions: {},
                gridApi: null,
                outbounds: [],
                columnDefs: null,
                defaultColDef: {
                    sortable: true,
                    editable: false,
                    resizable: true,
                    suppressMenu: true,
                    autoHeight: true,
                    wrapText: true
                },
                selectedId: null,
                isLoading: false,

                warehouse: {
                    data: [],
                    form: {
                        warehouse_id: ''
                    }
                },
                warehouseSelectionModal: false,
                vendorWarehouseModal: false,
                selectedOutbound: null,
                filterReference: '',
                filterStatus: 'ongoing',
            };
        },

        computed: {
            paginationPageSize() {
                if(this.gridApi) return this.gridApi.paginationGetPageSize()
                else return 50
            },
            totalPages() {
                if(this.gridApi) return this.gridApi.paginationGetTotalPages()
                else return 0
            },
            currentPage: {
                get() {
                    if(this.gridApi) return this.gridApi.paginationGetCurrentPage() + 1
                    else return 1
                },
                set(val) {
                    this.gridApi.paginationGoToPage(val - 1);
                }
            }
        },

        watch: {
            isLoading: function () {
                if (this.isLoading) {
                    this.$vs.loading();
                } else {
                    this.$vs.loading.close();
                }
            },

            currentPage() {
                this.fetchData();
            },
        },

        methods: {
            onSelectionChanged() {
                var selectedRows = this.gridApi.getSelectedRows();
                var selectedRowsString = "";
                selectedRows.forEach(function (selectedRow, index) {
                    if (index !== 0) {
                        selectedRowsString += ", ";
                    }
                    selectedRowsString += selectedRow.id;
                });
                this.selectedId = selectedRowsString;
            },

            processOnDblClick(params) {
                if (this.useVendorWarehouseModal) {
                    this.openVendorWarehouseModal(params);
                } else if (this.skipWarehouseSelection) {
                    this.skipToCommit(params);
                } else {
                    this.openWarehouseSelection();
                }
            },

            skipToCommit(params) {
                const outboundId = params.data.id;
                const origin = params.data.origin;
                const warehouseId = origin && origin.warehouse ? origin.warehouse.id : null;

                if (!warehouseId) {
                    this.$vs.notify({
                        color: "danger",
                        title: "Error",
                        text: "Warehouse ID not found for this outbound item",
                    });
                    return;
                }

                this.selectedId = outboundId;

                this.$router.push({
                    name: this.commitRouteName,
                    params: {
                        outbound_id: outboundId,
                        warehouse_id: warehouseId,
                    },
                });
            },

            openWarehouseSelection() {
                this.isLoading = true;

                this.fetchDropdowns().then(() => {
                    this.isLoading = false;

                    this.warehouseSelectionModal = true
                }).catch(() => {
                    this.isLoading = false;

                    this.$vs.dialog({
                        type: "confirm",
                        color: "danger",
                        title: "Server Error",
                        text: "Try again?",
                        accept: () => {
                            this.openWarehouseSelection();
                        },
                        acceptText: "Yes",
                        cancelText: "No",
                    });
                });
            },

            openVendorWarehouseModal(params) {
                this.selectedOutbound = params.data;
                this.vendorWarehouseModal = true;
            },

            submitVendorWarehouse(vendorData) {
                this.isLoading = true;

                this.$http.post(`/tvd/outbound/outbounddelivery/goodIssueVendorWarehouse/${vendorData.outbound_id}`).then((res) => {
                    this.isLoading = false;
                    this.vendorWarehouseModal = false;

                    this.$vs.notify({
                        color: "success",
                        title: "Success",
                        iconPack: "feather",
                        icon: "icon-check",
                        text: res.data.message || "Vendor warehouse confirmed successfully",
                    });

                    // Refresh list data
                    this.fetchData();
                }).catch(() => {
                    this.isLoading = false;
                    this.$vs.dialog({
                        type: "confirm",
                        color: "danger",
                        title: "Server Error",
                        text: "Try again?",
                        accept: () => {
                            this.submitVendorWarehouse(vendorData);
                        },
                        acceptText: "Yes",
                        cancelText: "No",
                    });
                });
            },

            fetchData() {
                this.isLoading = true;

                const params = {
                    page: this.currentPage,
                    ...(this.includeCentralWarehouseParam && { is_central_warehouse: this.isCentralWarehouse ? 1 : 0 }),
                    ...(this.filterReference && { outbound_reference: this.filterReference }),
                    ...(this.filterStatus && { status_filter: this.filterStatus })
                }
                if (this.orderType) {
                    params.order_type = this.orderType
                }

                this.$http.get('/tvd/outbound/deliveryorder/outbound', {
                    params
                }).then(res => {
                    this.outbounds = res.data.data;
                    this.isLoading = false;
                }).catch(() => {
                    this.isLoading = false;

                    this.$vs.dialog({
                        type: "confirm",
                        color: "danger",
                        title: "Server Error",
                        text: "Try again?",
                        accept: () => {
                            this.fetchData();
                        },
                        acceptText: "Yes",
                        cancelText: "No",
                    });

                    return;
                });
            },

            applyFilter() {
                this.currentPage = 1;
                this.fetchData();
            },

            clearFilter() {
                this.filterReference = '';
                this.filterStatus = 'ongoing';
                this.currentPage = 1;
                this.fetchData();
            },

            fetchDropdowns() {
                return this.$http.post("/master/warehouses", {
                    is_central_warehouse: this.isCentralWarehouse ? 1 : 0
                }).then(res => {
                    this.warehouse.data = res.data.data;
                });
            },

            processItem() {
                this.warehouseSelectionModal = false

                setTimeout(() => {
                    this.$router.push({
                        name: this.commitRouteName,
                        params: {
                            outbound_id: this.selectedId,
                            warehouse_id: this.warehouse.form.warehouse_id
                        },
                    });
                }, 250)
            },

            handleOutsideMouseDown(event) {
                const select = this.$refs.statusSelect;
                if (!select || !select.active) {
                    return;
                }

                const wrapper = this.$refs.statusWrapper;
                if (wrapper && wrapper.contains(event.target)) {
                    return;
                }

                if (event.target.closest && event.target.closest('.vs-select--options')) {
                    return;
                }

                if (typeof select.closeOptions === 'function') {
                    select.closeOptions();
                }
            },

            onGridReady(params) {
                params.api.sizeColumnsToFit();

                window.addEventListener('resize', () => {
                    params.api.sizeColumnsToFit();
                });
            }
        },

        mounted() {
            this.fetchData();
            this.gridApi = this.gridOptions.api;
            document.addEventListener('mousedown', this.handleOutsideMouseDown);
        },

        beforeDestroy() {
            document.removeEventListener('mousedown', this.handleOutsideMouseDown);
        },

        beforeMount() {
            this.columnDefs = [
                {
                    headerName: "Reference",
                    field: "outbound_reference",
                    filter: true,
                    flex: 0.1,
                },
                {
                    headerName: "Product",
                    field: "details",
                    flex: 1,
                    cellRenderer: params => {
                        const products = params.value

                        let cell = ``
                        products.forEach(item => {
                            cell = `
                                ${cell}
                                <li><span class="font-bold text-lg">[${item.committed_qty}/${item.base_qty}]</span> ${item.product.product_code}</li>
                            `
                        })

                        return `
                            <ul>
                                ${cell}
                            </ul>
                        `
                    }
                },
                {
                    headerName: "Route",
                    field: "destination.name",
                    flex: 0.15,
                    cellRenderer: params => {
                        const origin = params.data.origin ? params.data.origin.name : '-';
                        const destination = params.data.destination ? params.data.destination.name : '-';
                        return `${origin} → ${destination}`;
                    }
                },
                {
                    headerName: "Status",
                    field: "status_id",
                    cellRenderer: params => {
                        const statusId = params.value;

                        let badge = {
                            color: 'grey',
                            text: 'Uncommitted'
                        }

                        if (statusId === 3) {
                            badge.color = 'success'
                            badge.text = 'Fully Committed'
                        } else if (statusId === 2) {
                            badge.color = 'warning'
                            badge.text = 'Partially Committed'
                        }

                        return `
                            <span class="px-2 py-3 text-sm font-semibold text-white bg-${badge.color} rounded-full">${badge.text}</span>
                        `
                    },
                    filter: true,
                    flex: 0.1,
                }
            ];
        },
    };
</script>

<style>
    .ag-theme-material .ag-cell {
        white-space: normal !important;
    }
</style>
